import {
  AppointmentTimelineEvent,
  getTimelineWeekRange,
  Timeline,
  TimelineConfiguration,
  TimelineEvent,
  TimelineHistoricDetailsEvent,
  TimelineWeek,
} from '../../../models';
import {
  BaseTimeline,
  Creator,
  Timeline as OctopusTimeline,
  TimelineHistoric as OctopusTimelineHistoric,
  EntryHistoricDetails as OctopusEntryHistoricDetails,
  TimelineFieldOptionsResponse,
} from '../models/timeline';
import { ConfigurationMapper } from './configuration';
import { endOfDay, isAfter, isThisWeek, parseISO, startOfDay } from 'date-fns';
import { DefaultInteractions } from '../models';
import { BaseTimelineMapper } from './base-timeline';

export class TimelineConfigurationMapper extends ConfigurationMapper<
  TimelineFieldOptionsResponse,
  TimelineConfiguration
> {
  from(source: TimelineFieldOptionsResponse): TimelineConfiguration {
    const interactionLog = source.InteractionLog?.FieldOptions;
    const timeline = source.Timeline?.FieldOptions;

    return {
      categories:
        interactionLog?.Category?.Data?.map(this.mapValueAndName) ?? [],
      types: timeline?.Type?.map(this.mapInteractionType) ?? [],
      notes: timeline?.NoteType?.map(this.mapValueAndDisplay) ?? [],
      documents: timeline?.DocumentType?.map(this.mapValueAndDisplay) ?? [],
    };
  }
}

export class TimelineMapper extends BaseTimelineMapper<
  OctopusTimeline[],
  Timeline
> {
  from(source: OctopusTimeline[], reference?: Date): Timeline {
    const isCurrentWeek =
      reference && isThisWeek(reference, { weekStartsOn: 0 });
    const range = getTimelineWeekRange(reference);
    const events = this.getEvents(source);

    return new Timeline(range, [
      new TimelineWeek(isCurrentWeek ? 'last' : 'range', range.last, events),
      new TimelineWeek(
        isCurrentWeek ? 'current' : 'range',
        range.current,
        events,
      ),
      new TimelineWeek(isCurrentWeek ? 'next' : 'range', range.next, events),
    ]);
  }

  getEvents(source: OctopusTimeline[]): TimelineEvent[] {
    this.removeDuplicatedEmails(source);

    return source.map((event) => {
      return this.mapEvent(event);
    });
  }

  removeDuplicatedEmails(source: OctopusTimeline[]): void {
    type Emails = { [key: string]: OctopusTimeline[] };
    const interactions = source.filter(
      (item) => item.Type.Key === DefaultInteractions.EMAIL,
    );

    if (interactions.length) {
      const emails: Emails = interactions.reduce(function (group, interaction) {
        const key = `${interaction.StartDate.Value}-${interaction.Creator.Value}-${interaction.Subject}`;
        group[key] = group[key] || [];
        group[key].push(interaction);
        return group;
      }, {} as Emails);

      Object.keys(emails).forEach((key) => {
        const group = emails[key];
        if (group.length > 1 && group.some((item) => item.With)) {
          group.forEach((interaction) => {
            if (!interaction.With) {
              const index = source.findIndex(
                (item) => item.Key === interaction.Key,
              );
              source.splice(index, 1);
            }
          });
        }
      });
    }
  }

  mapEvent(source: OctopusTimeline): TimelineEvent {
    let event: TimelineEvent = {
      key: source.Key ?? '',
      type: 'interaction',
      subject: source.Subject,
      result: source.Result?.DisplayValue,
      description: source.Description ? source.Description.trim() : undefined,
      source: source.Source,
      users: this.mapUsers(source),
      category: !source.Category ? '' : source.Category.DisplayValue,
      isRecurring: source.Recurring,
      with: this.mapWith(source),
      regarding: this.mapRegarding(source),
      date: {
        value: parseISO(source.StartDate.Value),
        display: source.StartDate.DisplayValue,
      },
      endDate: source.EndDate
        ? {
            value: parseISO(source.EndDate.Value),
            display: source.EndDate.DisplayValue,
          }
        : undefined,
      priority: source.Priority?.DisplayValue,
      private: !source.SecStatus.CanRead,
      secStatus: source?.SecStatus,
      creator: {
        display: source.Creator?.DisplayValue,
        value: source.Creator?.Value,
      },
    };

    event = this.mapInteraction(event, source);

    if (DefaultInteractions.isNote(source.Type.Key)) {
      return this.mapNote(event, source);
    }

    if (DefaultInteractions.isAppointment(source.Type.Key)) {
      return this.mapAppointment(event, source);
    }

    if (DefaultInteractions.isTask(source.Type.Key)) {
      return this.mapTask(event, source);
    }

    if (DefaultInteractions.isEmail(source.Type.Key)) {
      return this.mapEmail(event, source);
    }

    if (DefaultInteractions.isDocument(source.Type.Key)) {
      return this.mapDocument(event, source);
    }

    return event;
  }
}

export class TimelineHistoricMapper extends BaseTimelineMapper<
  OctopusTimelineHistoric[],
  TimelineEvent[]
> {
  from(source: OctopusTimelineHistoric[]): TimelineEvent[] {
    return source.map((event) => {
      return this.mapEvent(event);
    });
  }

  fromDetails(
    source: OctopusEntryHistoricDetails[],
  ): TimelineHistoricDetailsEvent {
    const firstSource: OctopusEntryHistoricDetails | null = source.length
      ? source[0]
      : null;
    const result = {
      key: firstSource?.Key,
      subject: firstSource?.Subject,
    } as TimelineHistoricDetailsEvent;

    result.description = firstSource?.Description ?? firstSource?.Text;
    result.descriptionRichText =
      firstSource?.RichText ?? firstSource?.DescriptionRichText;
    result.isRichText =
      !!firstSource?.RichText?.length ||
      !!firstSource?.DescriptionRichText?.length;
    return result;
  }

  private mapEvent(source: OctopusTimelineHistoric): TimelineEvent {
    const withs = this.mapWiths(source);

    let event: TimelineEvent = {
      key: source.Key ?? '',
      type: 'interaction',
      subject: source['$LEFT(Subject, 200)'],
      description: source['$LEFT(Description, 200)']
        ? source['$LEFT(Description, 200)'].trim()
        : undefined,
      source: source.Source,
      isRecurring: source.Recurring,
      with: withs?.[0],
      withs: withs,
      regarding: this.mapRegarding(source),
      date: {
        value: source.StartDate.Value,
        display: source.StartDate.DisplayValue,
      },
      users: this.mapUsers(source),
      private: !source.SecStatus.CanRead,
      secStatus: source?.SecStatus,
      category: !source.Category ? '' : source.Category.DisplayValue,
      creator: {
        display: source.Creator?.DisplayValue,
        value: source.Creator?.Value,
      },
      isRichText: false,
    };

    event = this.mapInteraction(event, source);

    if (DefaultInteractions.isNote(source.Type.Key)) {
      return this.mapNote(event, source);
    }

    if (DefaultInteractions.isAppointment(source.Type.Key)) {
      return this.mapAppointment(event, source);
    }

    if (DefaultInteractions.isTask(source.Type.Key)) {
      return this.mapTask(event, source);
    }

    if (DefaultInteractions.isEmail(source.Type.Key)) {
      return this.mapEmail(event, source);
    }

    if (DefaultInteractions.isDocument(source.Type.Key)) {
      return this.mapDocument(event, source);
    }

    return event;
  }

  override mapAppointment(
    event: TimelineEvent,
    source: BaseTimeline & Creator,
  ): AppointmentTimelineEvent {
    const isDone = source.Completed?.Value;
    const hasTime = isAfter(event.date.value, startOfDay(event.date.value));
    return {
      ...event,
      type: 'appointment',
      isDone,
      isOverdue:
        !isDone &&
        isAfter(
          new Date(),
          hasTime ? event.date.value : endOfDay(event.date.value),
        ),
      hasTime,
      attendees: source.AbEntries?.length
        ? source.AbEntries.filter((user) => user.Key !== event.with?.id).map(
            (entry) => this.mapRelatedEntry('addressBook', entry),
          )
        : [],
    };
  }
}
